// Plain constants about the New York slip opinion archive, shared by the API
// and the browser. Values, not types — so keep this free of db/ and lib/
// imports, or the bundle picks them up.

export const NY_COURT_FAMILIES = [
  { value: "ctapp", label: "Court of Appeals" },
  { value: "ad1", label: "Appellate Division, 1st Dept" },
  { value: "ad2", label: "Appellate Division, 2nd Dept" },
  { value: "ad3", label: "Appellate Division, 3rd Dept" },
  { value: "ad4", label: "Appellate Division, 4th Dept" },
  { value: "at1", label: "Appellate Term, 1st Dept" },
  { value: "at2", label: "Appellate Term, 2nd Dept" },
  { value: "misc", label: "Trial courts" },
] as const

export const NY_COURT_LABELS: Record<string, string> = Object.fromEntries(
  NY_COURT_FAMILIES.map((c) => [c.value, c.label]),
)

// Adjudication only makes sense on law that is still live, so the picker stops
// at 2020.
export const NY_CASE_MIN_YEAR = 2020

export const nyCaseYears = (): number[] => {
  const latest = Math.max(NY_CASE_MIN_YEAR, new Date().getFullYear())
  return Array.from({ length: latest - NY_CASE_MIN_YEAR + 1 }, (_, i) => latest - i)
}

// ---------------------------------------------------------------------------
// LLM-generated categories (see .scripts/ny_cases_classify.py, which owns the
// identical lists — keep the two in step).
// ---------------------------------------------------------------------------

// Sparse on purpose: a new kind can be slotted between two existing ones
// without re-ranking any stored row. Declaration order is precedence order,
// and question_rank holds the value for question_kinds[0].
export const QUESTION_RANK = {
  constitutional_question: 10,
  statutory_interpretation: 20,
  precedent_interpretation: 30,
  settled_law_application: 40,
  discretion: 50,
  ministerial: 60,
  unclear: 70,
} as const

export type QuestionKind = keyof typeof QUESTION_RANK
export const QUESTION_KINDS = Object.keys(QUESTION_RANK) as QuestionKind[]

export const POSTURES = [
  "motion_to_dismiss",
  "summary_judgment",
  "trial_or_hearing",
  "discovery",
  "default_or_vacatur",
  "sentencing_or_plea",
  "appeal_from_judgment",
  "other_posture",
] as const
export type Posture = (typeof POSTURES)[number]

export const SUBJECT_AREAS = [
  "criminal",
  "family_and_matrimonial",
  "landlord_tenant_and_housing",
  "real_property_and_foreclosure",
  "personal_injury_and_torts",
  "insurance_and_no_fault",
  "contracts_and_commercial",
  "employment_and_labor",
  "estates_and_trusts",
  "administrative_and_article_78",
  "civil_procedure_and_attorney_discipline",
  "municipal_tax_and_election",
  "other_subject",
] as const
export type SubjectArea = (typeof SUBJECT_AREAS)[number]

// snake_case enum -> label. "article_78" and "no_fault" must not lose their
// numbers or hyphenation, so they are spelled out rather than title-cased.
const SPECIAL: Record<string, string> = {
  administrative_and_article_78: "Administrative & Article 78",
  insurance_and_no_fault: "Insurance & no-fault",
  civil_procedure_and_attorney_discipline: "Civil procedure & discipline",
  municipal_tax_and_election: "Municipal, tax & election",
  other_subject: "Other",
  other_posture: "Other",
}

export const labelOf = (v: string): string =>
  SPECIAL[v] ?? (v.replace(/_and_/g, " & ").replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase()))
